import { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { SUPPLIER_REPORTS_ROUTE } from "../../../../../../constants/routes"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { AccountingZReportsQuery, fetchZReports, FetchZReportsResponse } from "../zReportsApi"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { MRT_AggregationFn, MRT_ColumnDef } from "material-react-table"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { ZReportFlat } from "../zReportsTypes"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { DateTime } from "luxon"
import { useSupplier } from "../../../../supplierHooks"
import { SelectMerchantAutocomplete } from "../../components/inputFilters/SelectMerchantAutocomplete"
import { useExportZReports } from "../../../../../merchant/accounting/accounting/zReport/hooks/useExportZReports"
import { detectCurrency, zeroDinero } from "../../../../../currency/currencyLib"
import { DKK } from "@dinero.js/currencies"
import { MonetaryAmount } from "../../../../../currency/currencyTypes"
import { add, Dinero } from "dinero.js"
import {
    DateType,
    SelectDateTypeFilter,
} from "../../../../../../shared/components/filters/selectDateTypeFilter/SelectDateTypeFilter"
import { type DtDateRange, StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"

const transformData = (data: FetchZReportsResponse): ZReportFlat[] => {
    return data.zReports.flatMap((report) => {
        return report.channels.map((channel) => {
            return {
                date: report.date,
                ...channel,
            }
        })
    })
}

const sumAmounts: MRT_AggregationFn<ZReportFlat> = (columnId, _leafRows, childRows) => {
    // TODO: This should come from default currency for entity
    const fallbackCurrency = DKK

    const currency = detectCurrency(childRows, (row) => row.getValue(columnId) as MonetaryAmount, fallbackCurrency)
    const zero = zeroDinero(currency)

    return childRows.reduce((sum, next) => {
        const nextAmount = next.getValue<Dinero<number> | undefined>(columnId) ?? zero
        console.log("next amount: ", nextAmount)
        return add(sum, nextAmount)
    }, zero)
}

export function ZReports() {
    const { l10n } = useAppLocalization()
    const { supplierId } = useSupplier()

    const [dateType, setDateType] = useState<DateType>(DateType.ORDER_DATE)
    const [merchantId, setMerchantId] = useState<string | undefined>()
    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState<DtDateRange>({
        startDate: startDate,
        endDate: endDate,
    })

    const zReports = useApiQuery(fetchZReports, {
        queryName: AccountingZReportsQuery.Z_REPORTS_LIST,
        dependencies: {
            supplierId,
            merchantId: merchantId ?? "",
            startDate: dateState.startDate.toISODate(),
            endDate: dateState.endDate.toISODate(),
            dateType,
        },
        enabled: Boolean(merchantId),
        select: transformData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(zReports)

    const columns = useMemo<MRT_ColumnDef<ZReportFlat>[]>(
        () => [
            columnFactory.dateColumn(
                {
                    header: l10n.getString("supplier-accounting-z-reports-table-date-column"),
                    accessorKey: "date",
                },
                (row) => row.date,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("supplier-accounting-z-reports-table-pos-terminal-column"),
                accessorKey: "channel.displayText",
                enableGrouping: false,
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("supplier-accounting-z-reports-table-amount-column"),
                    enableGrouping: false,
                    aggregationFn: sumAmounts,
                    AggregatedCell: ({ cell, row }) => {
                        const amount = cell.getValue<Dinero<number> | undefined>()
                        const displayValue = amount ? columnFactory.moneyFactory.format(amount) : ""
                        return <>{displayValue}</>
                    },
                },
                (row) => row.sales,
                true,
            ),
        ],
        [l10n, columnFactory],
    )

    const exportReports = useExportZReports({
        data: zReports.data,
        startDate: dateState.startDate || DateTime.now(),
        endDate: dateState.endDate || DateTime.now(),
    })

    return (
        <>
            <LocalizedStrict id="supplier-accounting-z-reports-page-header" attrs={{ title: true }}>
                <PageHeader page={Page.SUPPLIER_Z_REPORTS} title="Z-reports" icon={<FontAwesomeIcon icon={faShop} />}>
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="supplier-nav-item-home">
                            <BreadcrumbLink to={SUPPLIER_REPORTS_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-nav-item-accounting">
                            <Breadcrumb>Accounting</Breadcrumb>
                        </LocalizedStrict>

                        <LocalizedStrict id="supplier-nav-item-z-reports">
                            <Breadcrumb>Z-reports</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <ApiQueryTable
                columns={columns}
                initialState={{
                    density: "spacious",
                    grouping: ["date"],
                    columnVisibility: {
                        "mrt-row-expand": true,
                    },
                }}
                enableRowActions={false}
                groupedColumnMode={false}
                enableExpanding={true}
                positionToolbarAlertBanner="none"
                renderToolbarInternalActions={({ table }) => (
                    <StandardTableFilter variant="right" table={table}>
                        <StandardExportButton onClick={() => exportReports()} />
                    </StandardTableFilter>
                )}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <SelectDateTypeFilter dateType={dateType} setDateType={setDateType} />
                        <StandardDateRangeFilter value={dateState} onChange={setDateState} />
                        <SelectMerchantAutocomplete
                            merchantId={merchantId}
                            setMerchantId={(merchantId) => {
                                setMerchantId(merchantId)
                            }}
                        />
                    </StandardTableFilter>
                )}
            />
        </>
    )
}
